import { useQuery } from "@tanstack/react-query";

import { api, type ReportScope } from "../../../lib/api";
import {
  withCardFilters,
  type DashboardLayout,
} from "../../../lib/dashboard-layout";
import { queryKeys } from "../../../lib/queryKeys";
import { DASHBOARD_STALE_MS } from "../../../lib/dashboard-refresh";
import { Select } from "../../ui/Select";
import { formatMinutes, rangeIsReady, toRangeValue } from "../RangeFilter";
import { CardChart, ReportCard } from "../card/ReportCard";
import { TicketCount } from "../card/TicketCount";
import type { ChartType } from "../card/chart-types";

/**
 * Where the desk is slow, pipeline by pipeline.
 *
 * Counted in **segments, not tickets**, and that is the whole report. A ticket
 * that went Quote → Support → RMA sat in three pipelines and made three
 * separate promises, each against that pipeline's own target. It is judged
 * three times, and the denominator for "how often did we meet the target" is
 * segments — so a busy ticket that crossed four desks counts four times,
 * because four different undertakings were given about it.
 *
 * Pipelines are whatever is configured; nothing here is hardcoded, so a new one
 * appears as a new row the day it is created.
 */

const CHARTS: ChartType[] = ["bar", "stacked-bar", "table"];

const SORTS = [
  { value: "worst-median", label: "Slowest first" },
  { value: "worst-sla", label: "Worst SLA first" },
] as const;

export function SegmentReport({
  layout,
  scope: pageScope,
  isManager,
  compareTeam,
}: {
  layout: DashboardLayout;
  /** Only a manager is offered the Agent filter; the server enforces it. */
  isManager: boolean;
  scope: ReportScope;
  compareTeam: boolean;
}) {
  const stored = layout.card("segments");
  /* The card's own Pipeline and Agent filters, folded into the page's
     scope so they reach both the query and its key. */
  const scope = withCardFilters(pageScope, stored);
  const range = toRangeValue(stored.range, { preset: "this-month" });
  const ready = rangeIsReady(range);
  /* Stored in the card's own `metric` slot -- it is a per-card choice like the
     chart type, and it belongs in the same arrangement that gets shared. */
  const sort = stored.metric === "worst-sla" ? "worst-sla" : "worst-median";

  const report = useQuery({
    queryKey: queryKeys.dashboardReport("segments", range, scope, {
      sort,
      compareTeam,
    }),
    queryFn: () => api.segmentReport(range, scope, { sort, compareTeam }),
    enabled: ready,
    staleTime: DASHBOARD_STALE_MS,
  });

  const rows = report.data?.pipelines ?? [];
  const series = rows.map((row) => ({
    key: row.name,
    label: row.name,
    color: row.color,
  }));

  const totalSegments = rows.reduce((sum, row) => sum + row.segments, 0);

  /*
   * Pipelines where the rate covers fewer segments than were answered, which
   * happens when a target was added or removed part-way through the period.
   * The denominator used to be printed on every row to cover this one case;
   * it is a footnote when it occurs instead.
   */
  const partlyMeasured = rows
    .filter((row) => row.measured > 0 && row.measured < row.segments)
    .map((row) => row.name);

  return (
    <ReportCard
      id="segments"
      title="Segment tracking & pipeline SLA"
      subtitle="One segment per pipeline a ticket passed through, judged against that pipeline's own target"
      layout={layout}
      offerAgents={isManager}
      charts={CHARTS}
      series={series}
      actions={
        <Select
          value={sort}
          onChange={(next) => layout.setCard("segments", { metric: next })}
          label="Sort pipelines by"
          options={SORTS.map((option) => ({ ...option }))}
        />
      }
      footnote={
        <>
          {/*
            Says what the two numbers are, now that the visible count is
            tickets and the rate underneath it is still per segment. Without
            this the two columns look like they should divide into each other.
          */}
          <p>
            {totalSegments.toLocaleString()} segments across {rows.length}{" "}
            pipelines. SLA is measured per segment, not per ticket: a ticket
            that moved between pipelines made a separate promise on each one.
          </p>
          {/*
            Only when it actually happens. A pipeline whose target was added or
            removed part-way through the period has segments on both sides of
            the change, and the rate then covers fewer than the ticket count
            suggests -- which is worth one sentence and not a column.
          */}
          {partlyMeasured.length > 0 ? (
            <p>
              {partlyMeasured.join(", ")} had a target for only part of the
              period, so the rate covers fewer segments than were answered.
            </p>
          ) : null}
        </>
      }
    >
      {({ chart, colorFor }) => {
        if (!ready) {
          return (
            <p className="py-6 text-center text-xs text-faint">
              Pick both dates.
            </p>
          );
        }

        if (rows.length === 0) {
          return (
            <p className="py-6 text-center text-xs text-faint">
              {report.isLoading
                ? "Loading…"
                : "Nothing was answered in this period."}
            </p>
          );
        }

        return (
          <div className="space-y-3">
            <CardChart
              chart={chart}
              rows={rows.map((row) => ({
                label: row.name,
                values: { median: row.medianMinutes },
              }))}
              series={[{ key: "median", label: "Median wait" }]}
              colorBy="row"
              colorFor={colorFor}
              format={formatMinutes}
            />

            {/* The chart carries the median; the table underneath carries what
                the chart cannot -- the target that applied, the tail, and how
                much of it was met. Those are the numbers a manager acts on and
                they do not survive being turned into bar heights. */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-faint">
                    <th className="px-2 py-1.5 font-medium">Pipeline</th>
                    <th className="px-2 py-1.5 text-right font-medium">Target</th>
                    <th className="px-2 py-1.5 text-right font-medium">Median</th>
                    <th className="px-2 py-1.5 text-right font-medium">p90</th>
                    <th className="px-2 py-1.5 text-right font-medium">p95</th>
                    <th className="px-2 py-1.5 text-right font-medium">SLA met</th>
                    <th className="px-2 py-1.5 text-right font-medium">Tickets</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.name} className="border-b border-line/60">
                      <td className="px-2 py-1.5">
                        <span className="inline-flex items-center gap-1.5">
                          <span
                            aria-hidden
                            className="size-1.5 rounded-full"
                            style={{ backgroundColor: colorFor(row.name) }}
                          />
                          <span className="text-content">{row.name}</span>
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-faint">
                        {/* An untimed pipeline is not a failing one. */}
                        {row.targetMinutes === null
                          ? "untimed"
                          : formatMinutes(row.targetMinutes)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-content">
                        {formatMinutes(row.medianMinutes)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-muted">
                        {formatMinutes(row.p90Minutes)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-muted">
                        {formatMinutes(row.p95Minutes)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {/* The percentage alone. The denominator used to be
                            printed beside it and simply restated the count in
                            the next column along. */}
                        {row.slaRate === null ? (
                          <span className="text-faint">—</span>
                        ) : (
                          <span
                            className={
                              row.slaRate >= 0.9
                                ? "text-sla-ok"
                                : row.slaRate >= 0.75
                                  ? "text-sla-warning"
                                  : "text-sla-breached"
                            }
                          >
                            {Math.round(row.slaRate * 100)}%
                          </span>
                        )}
                      </td>
                      <td
                        className="px-2 py-1.5 text-right tabular-nums text-muted"
                        title={`${row.segments} segment${row.segments === 1 ? "" : "s"} across ${row.tickets} ticket${row.tickets === 1 ? "" : "s"}`}
                      >
                        <TicketCount
                          count={row.tickets}
                          dimension="answered-pipeline"
                          value={row.pipelineId ? String(row.pipelineId) : ""}
                          label={`${row.name} — answered in this period`}
                          range={range}
                          scope={scope}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }}
    </ReportCard>
  );
}
